import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { COLOR_PALETTE } from 'app/constants/ColorPalette';
import { useClasses } from 'app/utils/css';

// Don't import Popper at the top level as popper should be instantiated lazily.
import type PopperJs from 'popper.js';

interface IProps {
  title: string;
  className?: string;
  children: ReactNode;
}

const Tooltip = ({ title, className, children }: IProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPopperLoaded, setIsPopperLoaded] = useState(false);

  const popperRef = useRef<PopperJs | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLSpanElement | null>(null);

  const isVisible = isHovered && isPopperLoaded;

  useEffect(
    () => {
      const applyPopper = async (tooltip: HTMLElement, container: HTMLElement) => {
        const { default: Popper } = await import('popper.js');

        if (popperRef.current) {
          popperRef.current.destroy();
        }
        popperRef.current = new Popper(container, tooltip, { placement: 'top' });

        if (!isPopperLoaded) {
          setIsPopperLoaded(true);
        }
      };

      if (isHovered && tooltipRef.current && containerRef.current) {
        applyPopper(tooltipRef.current, containerRef.current);
      }
    },
    [isHovered, isPopperLoaded, title],
  );

  useEffect(() => {
    return () => {
      if (popperRef.current) {
        popperRef.current.destroy();
      }
    };
  }, [popperRef]);

  /**
   * I use event bindings through properties, because
   * addEventListener events bubble from child elements
   * and causes unnecesarry rendes, makes async logic more
   * complex and makes tooltip to blink.
   */
  const hoverProps = {
    onMouseMove: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  };

  const css = useClasses({
    tooltip: {
      position: 'absolute',
      margin: '5px 0 5px 0',
      padding: '0',
      pointerEvents: 'none',
      transition: 'opacity 0.1s',
      opacity: isVisible ? 1 : 0,
      zIndex: 999999,
    },
    tooltipContent: {
      margin: 0,
      padding: '5px 8px 6px 8px',
      maxWidth: '500px',
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: 'normal',
      background: COLOR_PALETTE.grey.black,
      color: COLOR_PALETTE.grey.white,
    },
    tooltipArrow: {
      position: 'absolute',
      width: '6px',
      height: '6px',
      margin: '-3px 0 0 0',
      padding: '0',
      ':before': {
        content: '""',
        position: 'absolute',
        top: '0',
        left: '0',
        transform: 'rotate(45deg)',
        width: '6px',
        height: '6px',
        zIndex: 999998,
        background: COLOR_PALETTE.grey.black,
      },
      '[x-placement=bottom] &': {
        top: 0,
      },
    },
  });

  return (<>
    <span {...hoverProps} ref={containerRef} className={className}>
      {children}
    </span>
    <div ref={tooltipRef} className={css.tooltip}>
      <p className={css.tooltipContent}>{title}</p>
      <div className={css.tooltipArrow} x-arrow="true" />
    </div>
  </>);
};

export default Tooltip;
